import {
  Activity,
  Building2,
  Clock,
  CreditCard,
  Fingerprint,
  Globe,
  History,
  Inbox,
  KeyRound,
  LayoutDashboard,
  List,
  Network,
  ScrollText,
  Server,
  Settings,
  Shield,
  Sparkles,
  ToyBrick,
  Users,
  Wrench,
} from "lucide-react";
import { createElement, type SVGProps } from "react";
import { ORG, PROJECT } from "@/app/capabilities/permissions";
import { useOpenSuggestionsBadge } from "@/app/mcp-gateways/suggestions/api/hooks";
import type { SidebarData, SidebarItem } from "./types";

const RotatedNetwork = (props: SVGProps<SVGSVGElement>) =>
  createElement(Network, {
    ...props,
    className: [props.className, "rotate-180"].filter(Boolean).join(" "),
  });

/**
 * Rendered in the sidebar footer rather than a nav group, because it is gated on
 * an org capability plus `ORG.billing.read` resolved at render time.
 */
export const billingNavItem: SidebarItem = {
  id: "nav-billing",
  title: "Billing",
  url: "/billing",
  icon: CreditCard,
  // `/billing` redirects to `/billing/overview`, so an exact match never holds.
  activeOnChildRoutes: true,
};

export const data: SidebarData = {
  navOverview: [
    {
      id: "dashboard",
      title: "Dashboard",
      url: "/home",
      icon: LayoutDashboard,
    },
    {
      id: "nav-playground",
      title: "Playground",
      url: "/playground/chat",
      icon: Sparkles,
      activeOnChildRoutes: true,
      activeBasePath: "/playground",
      // Playground (chat + execute) exists only to run tools; gate the nav on
      // tools_execute to match the route guards, so it never shows to a caller
      // who can't run a tool.
      requiredPermission: PROJECT.tools.execute,
    },
    {
      id: "nav-inbox",
      title: "Inbox",
      url: "/suggestions",
      icon: Inbox,
      requiredPermission: PROJECT.mcpGateways.read,
      useBadgeCount: useOpenSuggestionsBadge,
    },
  ],
  navAgentDeveloper: [
    {
      id: "nav-organization",
      title: "Organization",
      url: "/organization",
      icon: Globe,
      // The page has independently gated sections for org metadata and org
      // members; show the nav entry when either section is reachable.
      requiredOrgPermission: [ORG.orgMetadata.read, ORG.members.read],
    },
    {
      id: "nav-projects",
      title: "Projects",
      url: "/projects",
      icon: Building2,
      items: [
        {
          id: "nav-members",
          title: "Members",
          url: "/project/members",
          icon: Users,
          // The page shows a members card and an invitations card, each gated on
          // its own read token; surface the nav item when either is readable.
          requiredPermission: [PROJECT.members.read, PROJECT.invitations.read],
        },
      ],
    },
    {
      id: "nav-tools",
      title: "Tool catalog",
      url: "/tools",
      icon: Wrench,
      requiredPermission: PROJECT.tools.read,
    },
    {
      id: "nav-mcp-gateways",
      title: "MCP gateways",
      url: "/mcp-gateways",
      activeOnChildRoutes: true,
      icon: RotatedNetwork,
      requiredPermission: PROJECT.mcpGateways.read,
    },
    {
      id: "nav-user-sources",
      title: "User sources",
      url: "/user-sources",
      icon: Fingerprint,
      requiredPermission: PROJECT.userSources.read,
    },
  ],
  navToolDeveloper: [
    {
      id: "nav-servers",
      title: "Servers",
      url: "/servers",
      activeOnChildRoutes: true,
      icon: Server,
      requiredPermission: PROJECT.servers.read,
    },
    {
      id: "nav-secrets",
      title: "Secrets",
      url: "/auth/secrets",
      activeOnChildRoutes: true,
      icon: KeyRound,
      requiredPermission: PROJECT.secrets.read,
    },
    {
      id: "nav-oauth-playground",
      title: "Connections",
      url: "/auth/oauth",
      icon: ToyBrick,
      // The group is shown when any sub-item is visible, so a caller with
      // connected-users or user-verification access still sees it. The parent's
      // own gate mirrors its `/auth/oauth` (OAuth providers) target, so the header
      // only links there for callers who can read auth providers — others get a
      // plain, non-clickable group header instead of a link into a 403.
      requiredPermission: PROJECT.authProviders.read,
      items: [
        {
          id: "nav-connections",
          title: "OAuth providers",
          url: "/auth/oauth",
          icon: List,
          activeOnChildRoutes: true,
          requiredPermission: PROJECT.authProviders.read,
        },
        {
          id: "nav-oauth-users",
          title: "Connected users",
          url: "/auth/connected-users",
          icon: Users,
          requiredPermission: PROJECT.userConnections.read,
        },
        {
          id: "nav-user-verification",
          title: "User verification",
          url: "/settings/user-verification",
          icon: Settings,
          requiredPermission: PROJECT.securitySettings.read,
        },
      ],
    },
  ],
  navSecurity: [
    {
      id: "nav-contextual-access",
      title: "Contextual Access",
      url: "/contextual-access",
      activeOnChildRoutes: true,
      icon: Shield,
      requiredPermission: PROJECT.plugins.read,
    },
    {
      id: "nav-observability",
      title: "Observability",
      url: "/audit-logs",
      icon: Activity,
      // The group is shown when any sub-item is visible, so a caller with only
      // execution access still sees it. The parent's own gate mirrors its
      // `/audit-logs` target, so the header only links there for callers who can
      // read audit logs — others get a plain, non-clickable group header.
      requiredPermission: PROJECT.auditLogs.read,
      items: [
        {
          id: "nav-audit-logs",
          title: "Audit Logs",
          url: "/audit-logs",
          icon: ScrollText,
          requiredPermission: PROJECT.auditLogs.read,
        },
        {
          id: "nav-organization-logging",
          title: "Logging Policy",
          url: "/organization/logging",
          icon: Clock,
          // Reading the policy is org metadata; changing it is gated in the form,
          // so someone who may only look still reaches the page.
          requiredOrgPermission: ORG.orgMetadata.read,
        },
        {
          id: "nav-tool-executions",
          title: "Tool Executions",
          url: "/tool-executions",
          icon: History,
          requiredPermission: PROJECT.executions.metadataRead,
        },
      ],
    },
  ],
};
